"use strict"

import React from "react"
import ReactDOM from "react-dom"

import { ButtonGroup } from "react-bootstrap"
import deepEqual from "deep-equal"
import memoize from "memoize-one"

import {
  toggleSingleMultiRunPlot,
  getRunTimeScales,
  getUnifiedPlotAxes,
  getCrossPlotProps,
  getMaxTimeSpan,
  getSiteReactors,
} from "../../graphing/resources/utils"
import DraggableGraphHeader from "../../graphing/resources/DraggableGraphHeader"
import LoadingIndicator from "../../graphing/resources/LoadingIndicator"
import NoGraphingDataMessage from "../../graphing/plotting/widgets/NoGraphingDataMessage"
import ToggleAnnotationsButton from "../../graphing/plotting/widgets/ToggleAnnotationsButton"
import ToggleRunLabelsButton from "../../graphing/plotting/widgets/ToggleRunLabelsButton"
import KeyBody from "../../graphing/plotting/GraphKeys/KeyBody"
import KeyHeader from "../../graphing/plotting/GraphKeys/KeyHeader"
import GraphImageModal, { GraphImageLegend } from "../../graphing/modals/GraphImageModal"
import DataAndCommentExportModals from "../../graphing/modals/DataAndCommentExportModals"
import DownloadPopoverMenu from "../../graphing/plotting/widgets/DownloadPopoverMenu"
import ZoomControlsPopover from "../../graphing/zooming/ZoomControlsPopover"
import { MultiRunViewDashboard } from "../AbstractDashboards/AbstractDashboard"
import PlotToolBar from "./PlotToolBar"
import RunHeaderBar from "./RunHeaderBar"
import HorzScroll, { graphIsShowing, getScrollingVariables } from "./HorzScroll"
import {
  id_accessor,
  isLTPM,
  multi_run_view as registration,
} from "../../../components/shared_components_registration"
import { equalArrays, parseDate } from "../../utils"
import PerGraphZoomScrollButtons from "../../graphing/zooming/ZoomControls/PerGraphZoomScrollButtons"
import ReportGenerationButton from "../../graphing/modals/ReportGenerationModal/ReportGenerationButton"

export const loadMultiRunPlots = (
  graphs = [],
  updateGraphs,
  runData = {},
  selectedRunsUpdated,
  prevProps = {},
  toggleMultiRunPlot,
  graphVariable = "graphs",
  useReactorColors = false,
  onlyReactorNames = false,
) => {
  const getLoadedRuns = (obj) =>
    Object.values(obj)
      .filter((r) => !r.loading && !r.err)
      .map((r) => r[id_accessor])

  const getMultiRunObs = (arr) =>
    [].concat.apply(
      [],
      arr.filter((g) => g.special === "multi").map((g) => g.observations),
    )

  const prevGraphs = prevProps[graphVariable] || []
  const multiRunPlotObs = getMultiRunObs(graphs)
  const prevMultiRunPlotObs = getMultiRunObs(prevGraphs)

  /*
   *  Very specific triggering conditions to avoid unnecessary graph update calls or race conditions
   *  with other parts of the code also requesting a graph set or observation detail update.
   */
  if (
    getLoadedRuns(runData).length > 0 && // At least one run loaded in
    graphs.filter((g) => g.special === "multi").length > 0 && // At least one graph is special="multi"
    Object.values(runData).filter((obj) => obj.loading).length === 0 && // No runs are currently loading in
    (selectedRunsUpdated || // Change in selected runs - make sure all runs are on plot
      !equalArrays(getLoadedRuns(runData), getLoadedRuns(prevProps.runData)) || // Change in state of runs loading in - new run added or existing run reloaded
      graphs.filter(
        (g) =>
          g.special === "multi" &&
          (g.observations || []).length === 0 &&
          (!g.restrictedRuns || (g.restrictedRuns || []).length > 0),
      ).length > 0 || // In case of added graph or change in favourite applied to page - a "multi" fave should have observations = []
      multiRunPlotObs.length > prevMultiRunPlotObs.length) // Triggers on adding new tag to plot - All other required obs plot updating should be handled elsewhere
  ) {
    /* Ensure all runs are present/removed from multi-run plots as necessary */
    let newGraphs = Object.assign([], graphs)
    graphs.forEach(
      (graph, i) =>
        graph?.special === "multi" &&
        toggleMultiRunPlot(
          i,
          graph,
          (newGraph, idx) => {
            newGraphs[idx] = newGraph
          },
          false,
          useReactorColors,
          onlyReactorNames,
        ),
    )

    !deepEqual(newGraphs, graphs) && updateGraphs(newGraphs)
  }
}

export default class MultiRunView extends MultiRunViewDashboard {
  static defaultProps = {
    plotSettings: {
      height: 250,
      margin: {
        top: 30,
        bottom: 80,
        left: 80,
        right: 85,
      },
      smallWidthHeight: 175,
    },
    chartWidthHeightChange: 1000,
    keyWidth: 325,
  }

  constructor(props) {
    super(props)

    this.state = {
      rootNode: document.getElementById("application-page"), // Run drag handlers are attached to this
      width: 600,
      graph_variables: {
        graphs: "graphs",
        updateGraphs: "updateGraphs",
      },

      // Related to horizontal graph scrolling in UI
      numOfGraphSelected: 3, // While props.graphs length is <= this number the graphs will flex to fill the space. Larger numbers of graphs will be available via scrolling.
      scrolledCount: 0, // To indicate the number/directionality of graphs that have been scrolled
    }

    this.baseTimesByRun = {}

    this.toggleSingleMultiRunPlot = toggleSingleMultiRunPlot.bind(this)
    this.overlayRuns = this.overlayRuns.bind(this)

    this._getPlotAndScale = this._getPlotAndScale.bind(this)
  }

  componentDidMount() {
    this.standardDidMount()

    const { runs, runData } = this.props
    this.ensureDataRequested(runs, runData, this._plotFactoryCache, false)
  }

  componentWillUnmount() {
    this.runDragRemoveListeners()
    this.standardWillUnmount()
  }

  componentDidUpdate(prevProps, prevState) {
    const { runData = {}, runs = [], graphs = [], updateGraphs } = this.props
    const { runData: prevRunData = {}, runs: prevRuns = [], graphs: prevGraphs = [] } = prevProps
    const { numOfGraphSelected, scrolledCount } = this.state
    const selectedRunsUpdated = !equalArrays(runs, prevRuns)

    if (selectedRunsUpdated || !deepEqual(runData, prevRunData)) {
      /* Ensure all tag data is loaded for all runs */
      this.ensureDataRequested(runs, runData, this._plotFactoryCache, false)
    }

    const graphNum = graphs.length
    if (
      graphNum !== prevGraphs.length &&
      scrolledCount !== 0 &&
      (graphNum <= numOfGraphSelected || scrolledCount < numOfGraphSelected - graphNum)
    ) {
      /*
       Update scrolled location (if necessary) to better match new number of plots (e.g. if user was
       scrolled on a view with 30 plots and switched to a 5 plot favorite we need to reset the
       scrolling so the graphs aren't wildly scrolled out of view)
      */
      this.setState({ scrolledCount: 0 })
    }

    runs.length > 0 &&
      loadMultiRunPlots(
        graphs,
        updateGraphs,
        runData,
        selectedRunsUpdated,
        prevProps,
        this.toggleSingleMultiRunPlot,
        "graphs",
        false,
      )
  }

  overlayRuns(op, index, plotEntry) {
    this.toggleSingleMultiRunPlot(index, op, plotEntry.plotConfigUpdater, op.special === "multi", false)
  }

  _getDisplayMin(availableRuns, plotEntry, axis) {
    return plotEntry.plot.special === "multi" && axis === "time"
      ? parseDate(Math.min(...availableRuns.map((run) => run.inoculated_on)))
      : null
  }

  // selectedMutation, frequencyThreshold passed purely to trigger an update of memoized getPlotAndScale outputs
  _getPlotAndScale(
    plotFactories,
    availableRuns,
    timeExtractor,
    alignment,
    selectedMutation,
    frequencyThreshold,
  ) {
    const viewerState = { ...this.props, ...this.state }

    const runBatons = this._getRunBatonVarsPerPlotKey(
      plotFactories,
      availableRuns,
      timeExtractor,
      viewerState,
      this.runBatons,
    )
    this.runBatons = runBatons
    const axesByPlot = getUnifiedPlotAxes(plotFactories, availableRuns, runBatons, this._getDisplayMin)
    const timeScales = getRunTimeScales(plotFactories, availableRuns, timeExtractor, viewerState, runBatons)
    const crossPlotProps = getCrossPlotProps(plotFactories, availableRuns, runBatons)
    const maxTimeSpan = getMaxTimeSpan(timeScales, availableRuns, timeExtractor, alignment)

    return [axesByPlot, timeScales, crossPlotProps, maxTimeSpan]
  }

  /* Memoize functions to remember the last passed arguments and result.
   *  If a function is next called with the same arguments then the previous result is returned without
   *  rerunning the function unnecessarily.  Passing in deepEqual to check equality of more complex arguments. */
  makePlotList = memoize(registration.makePlotList, deepEqual)
  getPlotAndScale = memoize(this._getPlotAndScale, deepEqual)

  render() {
    const {
      updateRunList,
      runs = [],
      runData = {},
      graphs = [],
      timeExtractor = {},
      alignment = {},
      updateGraphs,
      queryTags = [],
      special_graphs = [],
      plotSettings,
      minTime,
      maxTime,
      zoomConfig,
      report_metrics = [],
      perRunTimeOffsets,
      selectedMutation, // Mutation Plot Related
      frequencyThreshold, // Mutation Plot Related
      intl,
      keyWidth,
      chartWidthHeightChange,
    } = this.props
    const { numOfGraphSelected, scrolledCount, width } = this.state

    const { height, smallWidthHeight, margin } = plotSettings

    const plotFactories = this.getPlotFactories(graphs, updateGraphs, this._plotFactoryCache)
    this._plotFactoryCache = plotFactories

    const availableRuns = this.getAvailableRuns(runs, runData),
      loadingRuns = availableRuns.filter((r) => r.loading),
      multiRunObsTypes = availableRuns.map((r) => r.observationTypes || {}),
      plotList = this.makePlotList(special_graphs, queryTags, graphs, multiRunObsTypes),
      /* Get union of available tags across all passed runs */
      allObservationTypes = this.makeObservationList(...multiRunObsTypes) || {},
      /* Get chart and panel widths for plots */
      [graphNum, effectiveGraphNum, panelWidth] = getScrollingVariables(graphs, width, numOfGraphSelected),
      chartWidth = Math.max(50, panelWidth - margin.left - margin.right),
      plotHeight = width < chartWidthHeightChange ? smallWidthHeight : height,
      runHeaderWidth = 40

    const [axesByPlot, timeScalesByRun, crossPlotProps, maxTimeSpan] = this.getPlotAndScale(
      plotFactories,
      availableRuns,
      timeExtractor,
      alignment,
      selectedMutation,
      frequencyThreshold,
    )

    const [xScales, _, xAxisParameters] = this.configureXAxisPerRun(
      availableRuns,
      timeScalesByRun,
      timeExtractor,
      maxTimeSpan,
      chartWidth,
      0,
      perRunTimeOffsets,
      minTime,
      maxTime,
      alignment,
      (run_id) => this.batonForRun(run_id),
      false,
    )

    const overallBaseTime = Object.values(this.baseTimesByRun).sort((d1, d2) => d1 - d2)[0]

    const dragRun =
      this.state.dragRunMoved &&
      this.state.dragRunIndex !== undefined &&
      runData[runs[this.state.dragRunIndex]]

    const colWidthStyle = { flex: `0 0 ${panelWidth}px` }

    const siteReactors = getSiteReactors(runData, runs)

    return (
      <>
        {this.state.dragPlotMoved && (
          <DraggableGraphHeader
            plot={graphs[this.state.dragPlotIndex]}
            portal={this.dragObjectHolder}
            px={this.state.dragPlotCurrentX - this.state.dragPlotOffsetX}
            py={this.state.dragPlotCurrentY - this.state.dragPlotOffsetY}
            panelWidth={`${Math.min(panelWidth, keyWidth)}px`}
          />
        )}

        {dragRun &&
          ReactDOM.createPortal(
            <RunHeaderBar
              run={dragRun}
              intl={intl}
              width={runHeaderWidth}
              px={this.state.dragRunCurrentX - this.state.dragRunOffsetX}
              py={this.state.dragRunCurrentY - this.state.dragRunOffsetY}
            />,
            this.dragObjectHolder,
          )}

        {this.timeWarning && loadingRuns.length === 0 && <NoGraphingDataMessage intl={intl} />}

        {/* Sense width of page */}
        <div style={{ display: "flex" }}>
          <div style={{ flex: `${runHeaderWidth}px 0 0` /* Dummy to match width of RunHeaderBar */ }} />
          <div style={{ flex: 1 }} ref={this.widthSensorRef} />
          <div style={{ flex: "57px 0 0" /* Dummy to allow for space for width of PlotToolBar */ }} />
        </div>

        <PlotToolBar
          plotList={plotList}
          graphs={graphs}
          updateGraphs={updateGraphs}
          runs={runs}
          updateRunList={updateRunList}
          intl={intl}
          extraContent={
            <>
              <ReportGenerationButton
                availableRuns={availableRuns}
                plotList={plotList}
                siteMetrics={report_metrics}
                siteReactors={siteReactors}
                intl={intl}
              />
              <HorzScroll
                graphNum={graphs.length}
                numOfGraphSelected={numOfGraphSelected}
                scrolledCount={scrolledCount}
                onGraphNumChange={(value) => {
                  this.setState({
                    numOfGraphSelected: value,
                    scrolledCount: 0,
                  })
                }}
                onScrollChange={(value) => this.setState({ scrolledCount: value })}
              />
            </>
          }
          siteReactors={siteReactors}
        />

        <div
          className={"scrollable-transition-ease-multirun"}
          style={{ paddingBottom: "200px", position: "relative", left: `${scrolledCount * panelWidth}px` }}
        >
          <div
            className={"flex-r"}
            style={{ alignItems: "flex-end" }}
            ref={(ref) => (this.keyHolderRef = ref)}
          >
            <div style={{ flex: `0 0 ${runHeaderWidth}px` /* Dummy to match run labels */ }} />
            {graphs.map((op, opIndex) => {
              const plotEntry = plotFactories[opIndex]
              /* Figure out if graph would be showing on a scrolled page */
              const graphShowing = graphIsShowing(scrolledCount, numOfGraphSelected, opIndex)

              /* Checking for any plot zoom */
              const zoomedPlots =
                availableRuns
                  .map((run) => {
                    const perGraphZoom = zoomConfig.zoomForPlot(run[id_accessor], op)
                    return !!(perGraphZoom.y || perGraphZoom.y2)
                  })
                  .filter((has_y_zoom) => has_y_zoom).length > 0

              return (
                <div key={opIndex} style={colWidthStyle}>
                  <div
                    className={"plot-key-div"}
                    style={{
                      maxWidth: `${keyWidth}px`,
                      marginBottom: "10px",
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}
                  >
                    <div onMouseDown={this.plotDragStart.bind(this, opIndex)}>
                      <KeyHeader
                        intl={intl}
                        placement={"bottom"}
                        index={opIndex}
                        updateGraphs={updateGraphs}
                        plotList={plotList}
                        graphNum={graphNum}
                        disabled={!graphShowing}
                        title={op?.title || registration.choose_graph_label(intl)}
                        siteReactors={siteReactors}
                      />
                    </div>
                    <KeyBody
                      intl={intl}
                      graphKey={op}
                      index={opIndex}
                      plotEntry={plotEntry}
                      graphs={graphs}
                      updateGraphs={updateGraphs}
                      observationTypes={allObservationTypes}
                      crossPlotProps={crossPlotProps[opIndex]}
                      overlayRuns={this.overlayRuns}
                      availableRuns={availableRuns}
                      toggleAvailable={!zoomedPlots}
                      keyShowing={false}
                      timeExtractor={timeExtractor}
                      axesAndRanges={Object.assign({}, axesByPlot[opIndex])}
                      baseTime={overallBaseTime}
                      siteReactors={siteReactors}
                    />
                  </div>
                </div>
              )
            })}
          </div>

          {availableRuns.map((run, runIdx) => {
            let exportBaseName = registration.getExportBaseName(run)
            const run_id = run[id_accessor]
            const baton = this.batonForRun(run_id)
            const { loading, err, annotations = [], actions = [], site_actions = [] } = run

            const content =
              loading || err ? (
                <div
                  key={runIdx}
                  style={{
                    flex: "1 1 0%",
                    // Row height is normally controlled by the "plot" panels.  To prevent loading rows
                    // collapsing, we'll set it explicitly to match the expected eventual size of the plots.
                    height: `${plotHeight + margin.top + margin.bottom}px`,
                  }}
                >
                  {loading ? (
                    <LoadingIndicator />
                  ) : (
                    <div style={{ marginLeft: "5px" }}>
                      <div className={"error-message"}>{err.toString()}</div>
                    </div>
                  )}
                </div>
              ) : (
                graphs.map((op, opIndex) => {
                  /* Figure out if graph would be showing on a scrolled page */
                  const graphShowing = graphIsShowing(scrolledCount, numOfGraphSelected, opIndex)
                  const plotEntry = plotFactories[opIndex],
                    plotFactory = plotEntry.plotFactory
                  const perGraphZoom = zoomConfig.zoomForPlot(run_id, op)

                  if (
                    plotFactory &&
                    (op?.special !== "multi" ||
                      (op?.special === "multi" && runIdx === 0) ||
                      (op?.special === "multi" &&
                        timeExtractor.name === "Timestamp" &&
                        alignment.name === "Day"))
                  ) {
                    const baseTime = this.baseTimesByRun[run_id]
                    const x = xScales[run_id]
                    const xAxisParams = xAxisParameters[run_id]
                    const crossPlotProp = crossPlotProps[opIndex]
                    const plotProp = baton.plotPropsByKey[plotEntry.plotKey]
                    const axesAndRanges = Object.assign({}, axesByPlot[opIndex])
                    if (axesAndRanges.time) {
                      axesAndRanges.time = [baseTime, timeExtractor.offsetDays(baseTime, maxTimeSpan)]
                    }

                    exportBaseName =
                      op && op.special === "multi"
                        ? op.title || op.label
                        : registration.getExportBaseName(run)

                    const modalName = `modal_${opIndex}_${runIdx}` // Unique modal identifier
                    const dataModalName = `Data_${modalName}`
                    const commentsModalName = `Comments_${modalName}`

                    const xAxisVariables = {
                      baseTime: xAxisParams.displayMin,
                      minTime: xAxisParams.minTime,
                      maxTime: xAxisParams.maxTime,
                      offset: perRunTimeOffsets?.[run_id],
                      maxFullTime: xAxisParams.maxTimeSpan,
                    }

                    // Variables passed to both GraphImageModal and plotFactory.Component
                    const sharedGraphSettings = {
                      zoomKey: run_id,
                      axesAndRanges: axesAndRanges,
                      x: x,
                      zoomRanges: perGraphZoom,
                      timeExtractor: timeExtractor,
                      offset: perRunTimeOffsets?.[run_id],
                      graphKey: op,
                      annotations: annotations,
                      actions: actions,
                      site_actions: site_actions,
                      runEventLabels: op.special === "multi" ? [] : run.runEventLabels || [],
                      runInoculationDate: run?.inoculated_on,
                    }
                    const unifiedActions =
                      op.special === "multi"
                        ? crossPlotProp?.crossRunActions
                        : run.getCombinedActions
                          ? run.getCombinedActions(run)
                          : []

                    return (
                      <div key={opIndex} style={colWidthStyle}>
                        {graphShowing && (
                          <>
                            <ButtonGroup
                              style={{
                                marginLeft: "10px",
                                marginTop: "5px",
                                marginBottom: "5px",
                              }}
                            >
                              <ZoomControlsPopover
                                placement={"right"}
                                intl={intl}
                                axesAndRanges={axesAndRanges}
                                zoomKey={run_id}
                                graphKey={op}
                                graphIndex={opIndex}
                                plotConfigUpdater={plotEntry.plotConfigUpdater}
                                baseTime={baseTime}
                                allowIndependentGraphZoom
                              />
                              <DownloadPopoverMenu
                                placement={"right"}
                                intl={intl}
                                graph_name={modalName}
                                data_name={dataModalName}
                                comment_name={commentsModalName}
                                actions={unifiedActions}
                                loading={loadingRuns}
                                op={op}
                                showGraphModal={this.showGraphModal}
                                popover={false}
                              />
                              <ToggleAnnotationsButton
                                annotations={annotations}
                                placement={"bottom"}
                                plotList={plotList}
                                exportBaseName={exportBaseName}
                                run={run}
                                intl={intl}
                              />
                              <ToggleRunLabelsButton
                                run={run}
                                exportBaseName={exportBaseName}
                                op={op}
                                plotImageSettings={xAxisVariables}
                                PlotImage={(width, height, x) => {
                                  const { runEventLabels, ...graphSettings } = sharedGraphSettings
                                  return (
                                    <>
                                      <plotFactory.Component
                                        {...graphSettings}
                                        {...plotProp}
                                        {...crossPlotProp}
                                        x={x}
                                        width={width}
                                        height={height}
                                        margin={{ top: 20, bottom: 80, left: 85, right: 90 }}
                                        useSVG
                                      />
                                      <div
                                        style={{ display: "flex", width: "100%", justifyContent: "center" }}
                                      >
                                        <GraphImageLegend
                                          intl={intl}
                                          graphKey={op}
                                          graphIndex={opIndex}
                                          plotEntry={plotEntry}
                                          observationTypes={allObservationTypes}
                                          crossPlotProps={crossPlotProp}
                                          width={"90%"}
                                        />
                                      </div>
                                    </>
                                  )
                                }}
                              />
                              {this.state[modalName] && (
                                <GraphImageModal
                                  intl={intl}
                                  onHide={() => this.hideGraphModal(modalName)}
                                  plotEntry={plotEntry}
                                  title={`${op.title || op.label}${
                                    op.special === "multi" ? ` - MultiRun` : ` - ${exportBaseName}`
                                  }`}
                                  observationTypes={allObservationTypes}
                                  width={chartWidth * effectiveGraphNum}
                                  graphIndex={opIndex}
                                  crossPlotProps={crossPlotProp}
                                  plotProps={plotProp}
                                  {...xAxisVariables}
                                  {...sharedGraphSettings}
                                />
                              )}
                              {!!(this.state[dataModalName] || this.state[commentsModalName]) && (
                                <DataAndCommentExportModals
                                  showDataModal={this.state[dataModalName]}
                                  showCommentsModal={this.state[commentsModalName]}
                                  onHideDataModal={() => this.hideGraphModal(dataModalName)}
                                  onHideCommentsModal={() => this.hideGraphModal(commentsModalName)}
                                  actions={unifiedActions}
                                  perGraphZoom={perGraphZoom}
                                  commonDataVars={[
                                    op.special === "multi" ? availableRuns : run,
                                    timeExtractor,
                                    ...x.domain(),
                                  ]}
                                  plotFactory={plotFactory}
                                  exportBaseName={exportBaseName}
                                  op={op}
                                  viewerState={{ ...this.props, ...this.state }}
                                />
                              )}
                            </ButtonGroup>

                            <PerGraphZoomScrollButtons
                              intl={intl}
                              axesAndRanges={axesAndRanges}
                              zoomRanges={perGraphZoom}
                              zoomConfig={zoomConfig}
                              extraStyle={{ margin: "0" }}
                            />

                            <plotFactory.Component
                              exportBaseName={exportBaseName}
                              width={chartWidth}
                              height={plotHeight}
                              margin={margin}
                              baseTime={baseTime}
                              forceReloadPlotData={() => this.forceReloadPlotData(plotEntry)}
                              {...sharedGraphSettings}
                              {...plotProp}
                              {...crossPlotProp}
                            />
                          </>
                        )}
                      </div>
                    )
                  } else {
                    {
                      /* no graph so consume with an empty div */
                    }
                    return <div key={opIndex} style={colWidthStyle} />
                  }
                })
              )

            return (
              <div key={run_id} className={"flex-r"} ref={(ref) => (this.runRowRefs[run_id] = ref)}>
                <RunHeaderBar
                  run={run}
                  intl={intl}
                  width={runHeaderWidth}
                  exportBaseName={exportBaseName}
                  onDragStart={this.runDragStart.bind(this, runIdx)}
                  scrollable_px={scrolledCount * panelWidth}
                  updateRunList={updateRunList}
                  runs={runs}
                  canDownloadRawData
                  canUnselectRun
                />
                {content}
              </div>
            )
          })}
        </div>
      </>
    )
  }
}
